import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Container, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { getPageName, getUiInterface } from "../../../locales/translationUtils";
import { getDetalheMinhaCopia, reenviarMinhaCopia } from "../../../services/emailLogService";
import { MinhaCopiaDetalhe } from "../../../types/minhaCopia";

/** Campo somente leitura, mesmo visual dos demais "ver detalhe" do sistema (ex.: ViewClientById). */
const Field: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    <Form.Control
      size="sm"
      readOnly
      value={value === undefined || value === null || value === "" ? "-" : value}
      style={{ width: "100%", backgroundColor: "#f0f0f0" }}
    />
  </Form.Group>
);

/**
 * Detalhe de uma liberação no módulo "Minhas Cópias": os campos de negócio
 * reconstruídos direto de Release/Cargo, em formato de campo - sempre disponíveis,
 * mesmo para liberações legadas. Quando há um e-mail vinculado, mostra também a cópia
 * fiel do HTML enviado e a opção de reenviar dentro da janela de 24h (ver
 * MinhasCopiasController/BuscaMinhaCopiaDetalheStrategy no back-end).
 */
const MinhasCopiasViewById: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [detalhe, setDetalhe] = useState<MinhaCopiaDetalhe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [confirmandoReenvio, setConfirmandoReenvio] = useState(false);
  const [reenviando, setReenviando] = useState(false);

  useEffect(() => {
    if (!id) throw new Error("ID inválido");

    const carregar = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await getDetalheMinhaCopia(Number(id));
        setDetalhe(data);
      } catch (err) {
        console.error("Erro ao carregar detalhe da liberação:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [id]);

  const handleReenviar = async () => {
    if (!detalhe) return;

    setReenviando(true);
    try {
      await reenviarMinhaCopia(detalhe.id);
      window.alert(t('carbonCopy.labels.resendSuccess'));
      setConfirmandoReenvio(false);
      // Recarrega: o reenvio bem-sucedido nao muda os campos estruturados, mas e bom
      // refletir qualquer efeito colateral (ex.: novo enviadoEm, mensagemErro zerada).
      const data = await getDetalheMinhaCopia(detalhe.id);
      setDetalhe(data);
    } catch (err: any) {
      const backendMessage = err?.response?.data?.messages?.[0];
      window.alert(backendMessage || t('carbonCopy.labels.resendError'));
    } finally {
      setReenviando(false);
    }
  };

  const navItems: NavBarItem[] = [
    { label: t('carbonCopy.labels.home'), route: "/", iconName: "house-door" },
    { label: t('carbonCopy.labels.back'), route: "/carbon-copies", iconName: "arrow-left" },
  ];

  if (loading) {
    return (
      <Container fluid>
        <NavBar items={navItems} />
        <div className="text-center mt-4"><Spinner animation="border" /></div>
      </Container>
    );
  }

  if (error || !detalhe) {
    return (
      <Container fluid>
        <NavBar items={navItems} />
        <div className="mt-4">
          <Alert variant="danger">{t('carbonCopy.labels.loadError')}</Alert>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <NavBar items={navItems} />
      <div className="mb-3"></div>
      <h2>{getPageName("minhasCopias")}</h2>

      {/* Cabecalho: campos que sempre existem, a liberacao e a fonte primaria agora. */}
      <Row className="mt-3">
        <Col md={3}>
          <Field
            label={t('carbonCopy.fields.liberadoEm')}
            value={detalhe.liberadoEm ? new Date(detalhe.liberadoEm).toLocaleDateString() : undefined}
          />
        </Col>
        <Col md={3}><Field label={t('carbonCopy.fields.liberadoPor')} value={detalhe.liberadoPor} /></Col>
        <Col md={3}><Field label={t('carbonCopy.fields.modal')} value={detalhe.modal} /></Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>{t('carbonCopy.fields.sucesso')}</Form.Label>
            <div>
              {detalhe.temEmailRegistrado ? (
                <Badge bg={detalhe.sucesso ? "success" : "danger"}>
                  {detalhe.sucesso ? getUiInterface('success') : getUiInterface('failure')}
                </Badge>
              ) : (
                <Badge bg="secondary">
                  <i className="bi bi-clock-history me-1"></i>
                  {t('carbonCopy.labels.legacy')}
                </Badge>
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>

      <h5>{t('carbonCopy.labels.release')}</h5>
      <Row>
        <Col md={4}><Field label={t('carbonCopy.fields.to')} value={detalhe.to} /></Col>
        <Col md={4}>
          <Field
            label={t('carbonCopy.fields.areaDeRemocao')}
            value={[detalhe.areaDeRemocao, detalhe.tipoAreaDeRemocao].filter(Boolean).join(" - ")}
          />
        </Col>
        <Col md={4}><Field label={t('carbonCopy.fields.tipoFrete')} value={detalhe.tipoFrete} /></Col>
      </Row>
      <Row>
        <Col md={6}>
          <Field
            label={t('carbonCopy.fields.aceitaSemOriginal')}
            value={detalhe.aceitaSemOriginal ? getUiInterface('yes') : getUiInterface('no')}
          />
        </Col>
        <Col md={6}>
          <Field
            label={t('carbonCopy.fields.aceitaComDiscrepancia')}
            value={detalhe.aceitaComDiscrepancia ? getUiInterface('yes') : getUiInterface('no')}
          />
        </Col>
      </Row>
      {detalhe.observacoes && (
        <Row>
          <Col md={12}><Field label={t('carbonCopy.fields.observacoes')} value={detalhe.observacoes} /></Col>
        </Row>
      )}

      <h5>{t('carbonCopy.labels.consignee')}</h5>
      <Row>
        <Col md={4}><Field label={t('carbonCopy.fields.consigneeNome')} value={detalhe.consigneeNome} /></Col>
        <Col md={4}><Field label={t('carbonCopy.fields.consigneeCnpj')} value={detalhe.consigneeCnpj} /></Col>
        <Col md={4}><Field label={t('carbonCopy.fields.consigneeEndereco')} value={detalhe.consigneeEndereco} /></Col>
      </Row>

      <h5>{t('carbonCopy.labels.cargas')} ({detalhe.cargas.length})</h5>
      <div className="mb-3" style={{ overflowX: "auto" }}>
        <Table striped bordered size="sm" responsive>
          <thead>
            <tr>
              <th>{t('carbonCopy.fields.descricao')}</th>
              <th>{t('carbonCopy.fields.cliente')}</th>
              <th>{t('carbonCopy.fields.fornecedor')}</th>
              <th>{t('carbonCopy.fields.armazem')}</th>
              <th>{t('carbonCopy.fields.poNumber')}</th>
              <th>{t('carbonCopy.fields.soNumber')}</th>
              <th>{t('carbonCopy.fields.wrNumber')}</th>
              <th>{t('carbonCopy.fields.dawb')}</th>
              <th>{t('carbonCopy.fields.weight')}</th>
              <th>{t('carbonCopy.fields.pieces')}</th>
              <th>{t('carbonCopy.fields.dgrGoods')}</th>
              <th>{t('carbonCopy.fields.dgr')}</th>
              <th>{t('carbonCopy.fields.invoices')}</th>
            </tr>
          </thead>
          <tbody>
            {detalhe.cargas.map((carga, index) => (
              <tr key={index}>
                <td>{carga.descricao || "-"}</td>
                <td>{carga.cliente || "-"}</td>
                <td>{carga.fornecedor || "-"}</td>
                <td>{carga.armazem || "-"}</td>
                <td>{carga.poNumber || "-"}</td>
                <td>{carga.soNumber || "-"}</td>
                <td>{carga.wrNumber || "-"}</td>
                <td>{carga.dawb || "-"}</td>
                <td>{carga.weight}</td>
                <td>{carga.pieces}</td>
                <td>{carga.dgrGoods ? getUiInterface('yes') : getUiInterface('no')}</td>
                <td>{carga.dgr ? getUiInterface('yes') : getUiInterface('no')}</td>
                <td>{carga.invoices.length > 0 ? carga.invoices.join(", ") : "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Enviado em/Para/Cco/Assunto vêm SEMPRE preenchidos (ver MinhaCopiaDetalheVM):
          exatos quando temEmailRegistrado, ou uma reconstrução provável a partir da
          própria liberação quando não há e-mail rastreado. Só o HTML de verdade
          (corpoOriginal) e o reenvio dependem de um e-mail realmente registrado - não dá
          pra reconstruir o HTML exato nem reenviar algo que nunca foi enviado. */}
      <h5>{t('carbonCopy.labels.originalCopy')}</h5>

      {!detalhe.temEmailRegistrado && (
        <Alert variant="warning">{t('carbonCopy.labels.informacoesProvaveis')}</Alert>
      )}

      <Row className="align-items-end">
        <Col md={4}>
          <Field
            label={t('carbonCopy.fields.enviadoEm')}
            value={detalhe.enviadoEm ? new Date(detalhe.enviadoEm).toLocaleString() : undefined}
          />
        </Col>
        <Col md={8} className="mb-3 d-flex justify-content-end align-items-center">
          {detalhe.temEmailRegistrado && (
            detalhe.podeReenviar ? (
              <Button variant="primary" onClick={() => setConfirmandoReenvio(true)}>
                <i className="bi bi-arrow-repeat me-1"></i>
                {t('carbonCopy.labels.resend')}
              </Button>
            ) : (
              <span className="text-muted" style={{ fontSize: "13px" }}>
                {t('carbonCopy.labels.resendWindowExpired')}
              </span>
            )
          )}
        </Col>
      </Row>

      {detalhe.temEmailRegistrado && !detalhe.sucesso && detalhe.mensagemErro && (
        <Alert variant="danger">{detalhe.mensagemErro}</Alert>
      )}

      <div className="mb-2">
        <strong>{getUiInterface('to')}:</strong> {detalhe.destinatarios}
      </div>
      {/* Cco só vem preenchido do back-end para ROLE_MASTER - o e-mail de todo
          mundo copiado na mesma liberação, não só o do dono do e-mail. */}
      {detalhe.cco && (
        <div className="mb-2">
          <strong>{getUiInterface('bcc')}:</strong> {detalhe.cco}
        </div>
      )}
      <div className="mb-2">
        <strong>{getUiInterface('subject')}:</strong> {detalhe.assunto}
      </div>

      {/* O corpo vem num iframe com srcDoc: é o próprio conteúdo gerado pelos templates
          do sistema (Scriban), não entrada de usuário, mas o iframe ainda assim isola
          o HTML da página em vez de injetá-lo direto no DOM - mesma cautela de
          ViewBodyModal (módulo EmailLog), sem custo aqui. Só existe pra e-mail
          realmente registrado: não há HTML exato pra mostrar de uma reconstrução. */}
      {detalhe.temEmailRegistrado && (
        <iframe
          title="minha-copia-body"
          srcDoc={detalhe.corpoOriginal}
          style={{ width: "100%", height: "50vh", border: "1px solid #d6dae4", borderRadius: 6, marginBottom: "1rem" }}
        />
      )}

      <Modal show={confirmandoReenvio} onHide={() => setConfirmandoReenvio(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('carbonCopy.labels.confirmResend')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('carbonCopy.labels.confirmResendMessage')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmandoReenvio(false)} disabled={reenviando}>
            {getUiInterface('no')}
          </Button>
          <Button variant="primary" onClick={handleReenviar} disabled={reenviando}>
            {reenviando ? <Spinner animation="border" size="sm" /> : getUiInterface('yes')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MinhasCopiasViewById;
